import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  Tabs,
  Tab,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Grid,
  Stack,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import get from 'lodash/get';

import * as API from 'api/addresses';
import InputLabel from 'components/InputLabel';
import { createAddress, createPolicyObject, deleteAddress, updateAddress } from 'store/objects/actions';
import { OBJECT_CATEGORIES } from 'enum/object';
import { DEFAULT_LOCATION, LOCATION_TYPE } from 'enum/location';
import { IP_TYPE } from 'enum/ip';

import DescriptionTab from '../components/DescriptionTab';
import { validationSchema } from './validationSchema';

const AddressForm = ({ object, policyField, onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { cusID: customerUUID, ecoID: ecosystemUUID } = useParams();

  const handleSave = async (values) => {
    try {
      const payload = {
        name: values.name,
        type: values.type,
        address: values.address,
        description: values.description,
        category: values.category,
        geolocation: {
          latitude: values.lat,
          longitude: values.lng,
        },
      };
      if (object?.uuid) {
        const result = await API.updateAddress({ customerUUID, ecosystemUUID, payload, uuid: object.uuid });
        dispatch(updateAddress({ ecosystemUUID, result }));
        enqueueSnackbar(`${values.name} (Address) has been updated`, { variant: 'success' });
      } else {
        const result = await API.createAddress({ customerUUID, ecosystemUUID, payload });
        result.element = 'address';
        dispatch(createAddress({ ecosystemUUID, result }));
        if (policyField) {
          dispatch(createPolicyObject({ field: policyField, object: result }));
        }
        enqueueSnackbar('New Address has been created', { variant: 'success' });
      }
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error saving object:', error);
      dispatch({ type: 'ADDRESS_FAILURE', payload: { message: error } });
    }
  };

  const [selectedTab, setSelectedTab] = useState(1);
  const { values, errors, touched, handleChange, handleSubmit, setFieldValue, isSubmitting } = useFormik({
    initialValues: {
      name: get(object, 'name', ''),
      type: get(object, 'type', IP_TYPE.IPv4),
      description: get(object, 'description', ''),
      address: get(object, 'address', ''),
      category: get(object, 'category', ''),
      // eslint-disable-next-line no-nested-ternary
      location: object
        ? object.geolocation
          ? LOCATION_TYPE.COORDINATES
          : LOCATION_TYPE.UNDEFINED
        : LOCATION_TYPE.COORDINATES,
      lat: get(object, 'geolocation.latitude', DEFAULT_LOCATION.LAT),
      lng: get(object, 'geolocation.longitude', DEFAULT_LOCATION.LNG),
    },
    validationSchema,
    onSubmit: handleSave,
  });

  useEffect(() => {
    setSelectedTab(1);
  }, [isSubmitting]);

  const handleDelete = async () => {
    if (!object?.uuid) return;
    try {
      await API.deleteAddress({ customerUUID, ecosystemUUID, uuid: object.uuid });
      dispatch(deleteAddress({ ecosystemUUID, uuid: object.uuid }));
      enqueueSnackbar(`${object.name} (Address) has been removed`, { variant: 'success' });
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error deleting object:', error);
      dispatch({ type: 'ADDRESS_DELETE_FAILURE', payload: { message: error } });
    }
  };

  const handleChangeType = (event, newType) => {
    setFieldValue('type', newType);
  };

  const handleChangeAddress = (event) => {
    const { value } = event.target;
    setFieldValue('address', value);
    if (!object?.uuid && (values.name === values?.address || values.name === '')) {
      setFieldValue('name', value);
    }
  };

  return (
    <div>
      <Box sx={{ px: 4, pt: 2, pb: 3 }}>
        <Tabs value={selectedTab} onChange={(_, val) => setSelectedTab(val)} sx={{ mb: 2.5 }}>
          <Tab
            label="Configuration"
            data-testid="tab_configuration"
            value={1}
            sx={{ fontSize: 16, fontWeight: 600, textTransform: 'none' }}
          />
          <Tab
            label="Descriptors"
            data-testid="tab_descriptors"
            value={2}
            sx={{ fontSize: 16, fontWeight: 600, textTransform: 'none' }}
          />
        </Tabs>
        <Paper elevation={0} sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 4 }}>
            {selectedTab === 1 && (
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <ToggleButtonGroup color="primary" value={values.type} exclusive name="type">
                    <ToggleButton value={IP_TYPE.IPv4} onClick={handleChangeType}>
                      IPv4
                    </ToggleButton>
                    <ToggleButton value={IP_TYPE.IPv6} onClick={handleChangeType}>
                      IPv6
                    </ToggleButton>
                    <ToggleButton value={IP_TYPE.FQDN} onClick={handleChangeType}>
                      FQDN
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel required>Name</InputLabel>
                  <TextField
                    fullWidth
                    name="name"
                    variant="standard"
                    placeholder="Name"
                    value={values.name}
                    onChange={handleChange}
                    error={Boolean(touched.name && errors.name)}
                    helperText={!!touched.name && errors.name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel required>Address</InputLabel>
                  <TextField
                    fullWidth
                    name="address"
                    variant="standard"
                    placeholder="Address"
                    value={values.address}
                    onChange={handleChangeAddress}
                    error={Boolean(touched.address && errors.address)}
                    helperText={!!touched.address && errors.address}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel required>Category</InputLabel>
                  <Autocomplete
                    options={OBJECT_CATEGORIES}
                    getOptionLabel={(option) => option.label}
                    value={OBJECT_CATEGORIES.find((cat) => cat.value === values.category)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <img src={option.icon} alt={option.label} style={{ width: 24, height: 24, marginRight: 8 }} />
                        {option.label}
                      </Box>
                    )}
                    onChange={(event, newValue) =>
                      handleChange({ target: { name: 'category', value: newValue?.value ?? '' } })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          id: 'select-category_address',
                        }}
                        variant="standard"
                        placeholder="Select category"
                        error={Boolean(touched.category && errors.category)}
                        helperText={!!touched.category && errors.category}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField disabled label="Type" fullWidth value="Address" />
                </Grid>
              </Grid>
            )}
            {selectedTab === 2 && (
              <DescriptionTab values={values} errors={errors} touched={touched} onChange={handleChange} />
            )}
          </Box>
        </Paper>
        <Box sx={{ pt: 3 }}>
          <Stack direction="row-reverse" justifyContent="space-between">
            <Button data-testid="btn-save_address" variant="contained" onClick={handleSubmit} sx={{ minWidth: 90 }}>
              {object?.uuid ? 'Save' : 'Add'}
            </Button>
            {object?.uuid && (
              <Button
                data-testid="btn-delete_address"
                variant="contained"
                color="error"
                onClick={handleDelete}
                sx={{ minWidth: 90 }}
              >
                Delete
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </div>
  );
};

AddressForm.propTypes = {
  object: PropTypes.object,
  policyField: PropTypes.string,
  onClose: PropTypes.func,
};

export default AddressForm;
